import { useAccept, useAutoPaste } from '@aigc/hooks';
import { InboxOutlined, VideoCameraTwoTone } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { Alert, App, Button, Checkbox, Form, Select, Upload } from 'antd';
import { RESET } from 'jotai/utils';
import { useEffect } from 'react';
import { useForm, Watch } from 'react-hook-form';
import { FormControl } from '../../components/form-control';
import { usePreviewFn } from '../../features/file/hooks';
import {
  toFileInputDtoList,
  toFileListFormValues,
} from '../../features/file/utils';
import { HeygenTargetLanguageSelect } from '../../features/heygen/components/heygen-target-language';
import { getBaseTaskListQueryKey } from '../../features/task/api';
import { TaskType } from '../../features/task/model';
import { longVideoTranslateVideo } from '../../features/video-translation/api';
import { schema } from '../../features/video-translation/schema';
import { useVideoTranslationPageData } from '../../features/video-translation/store';
import { VideoGenerationTaskTable } from '../../features/video/components/task-table';
import { createBeforeUpload, createUploadValidator } from '../file';
import {
  SubtitleRemovalMode,
  subtitleRemovalModeOptions,
} from './subtitle-removal-mode';

export const VideoTranslationPage = () => {
  const { message, notification } = App.useApp();
  const onPreview = usePreviewFn();
  const [pageData, setPageData] = useVideoTranslationPageData();

  const form = useForm({
    defaultValues: {
      removal_mode: SubtitleRemovalMode.智能擦除,
      regenerate_subtitles: false,
      regenerate_voice: false,
      remove_subtitles: false,
      target_languages: ['English'],
    },
    resolver: yupResolver(schema),
  });

  const { control, handleSubmit, reset, subscribe } = form;

  const videoTranslate = useMutation({
    mutationKey: getBaseTaskListQueryKey(),
    ...longVideoTranslateVideo,
  });

  useEffect(() => {
    return subscribe({
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        setPageData({
          ...values,
          video: toFileInputDtoList(values.video_file).at(0),
        });
      },
    });
  }, [subscribe, setPageData]);

  useEffect(() => {
    reset(
      {
        removal_mode: pageData.removal_mode ?? SubtitleRemovalMode.智能擦除,
        regenerate_subtitles: pageData.regenerate_subtitles ?? void 0,
        remove_subtitles: pageData.remove_subtitles ?? void 0,
        regenerate_voice: pageData.regenerate_voice ?? void 0,
        target_languages: pageData.target_languages ?? ['English'],
        video_file: !pageData.video
          ? null!
          : toFileListFormValues([pageData.video]),
      },
      { keepDefaultValues: true },
    );
  }, []);

  const handleTranslate = handleSubmit(
    async (values) => {
      const file = values.video_file[0]!;
      lfq('track', 'video.translate');
      const tasks = await videoTranslate.mutateAsync({
        lipsync: true,
        removal_mode: values.removal_mode,
        regenerate_subtitles: values.regenerate_subtitles,
        regenerate_voice: values.regenerate_voice,
        remove_subtitles: values.remove_subtitles,
        source_language: values.long_video_source_language as string,
        target_languages: values.target_languages,
        voice: values.long_video_voice as number,
        video: {
          name: file.name,
          type: file.type!,
          url: file.response!.file_url,
        },
      });

      message.success(`已提交 ${tasks.length} 个视频翻译任务`);
    },
    (error) => {
      console.error(error);
    },
  );

  const accept = useAccept(['video/*']);
  useAutoPaste('#uploader', {
    filter: accept.filter,
  });

  return (
    <div className="flex w-full flex-col divide-y divide-primary-200 overflow-y-auto sm:h-full sm:flex-row sm:divide-x sm:divide-y-0">
      {/* 左右布局，左侧操作面板，占据60% */}
      <div className="card animate-fade-in sm:overflow-auto xl:w-[480px]">
        <div className="space-y-6">
          <FormControl
            control={control}
            name="video_file"
            exact
            render={({ field }) => (
              <Upload.Dragger
                id="uploader"
                ref={field.ref}
                name={field.name}
                disabled={field.disabled}
                classNames={{ list: 'mt-2' }}
                listType="picture"
                fileList={field.value}
                maxCount={1}
                accept={accept}
                iconRender={() => <VideoCameraTwoTone />}
                beforeUpload={createBeforeUpload({
                  validator: createUploadValidator({ accept: accept.format }),
                  onError: ({ title, message }) => {
                    notification.error({ title, description: message });
                  },
                })}
                onPreview={onPreview}
                onChange={(info) => {
                  const { status } = info.file;

                  if (status === 'error') {
                    notification.error({
                      title: '错误',
                      description: `视频 ${info.file.name} 上传失败，请重试`,
                    });
                  }

                  field.onChange(info.fileList);
                }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  点击，拖拽或<span className="text-blue-500">粘贴</span>
                  上传视频
                </p>
              </Upload.Dragger>
            )}
          ></FormControl>
          <Form layout="inline">
            <FormControl
              control={control}
              name="remove_subtitles"
              render={({ field }) => {
                return (
                  <Checkbox {...field} checked={field.value ?? false}>
                    智能去字幕
                  </Checkbox>
                );
              }}
            ></FormControl>
            <FormControl
              control={control}
              name="regenerate_voice"
              render={({ field }) => {
                return (
                  <Checkbox {...field} checked={field.value ?? false}>
                    重新生成配音
                  </Checkbox>
                );
              }}
            ></FormControl>
            <Watch
              control={control}
              name="regenerate_voice"
              render={(checked) => {
                if (!checked) {
                  return null;
                }

                return (
                  <FormControl
                    control={control}
                    name="regenerate_subtitles"
                    render={({ field }) => {
                      return (
                        <Checkbox {...field} checked={field.value ?? false}>
                          重新生成字幕
                        </Checkbox>
                      );
                    }}
                  ></FormControl>
                );
              }}
            ></Watch>
          </Form>
          <Watch
            control={control}
            name="regenerate_voice"
            render={(checked) => {
              if (!checked) {
                return null;
              }

              return (
                <Alert
                  showIcon
                  type="warning"
                  title={
                    <span>
                      <span className="font-bold">重新生成配音</span>
                      要确保视频中存在人物说话的声音，否则无法生成
                    </span>
                  }
                ></Alert>
              );
            }}
          ></Watch>
          <Alert
            showIcon
            type="warning"
            title="预计15分钟左右，根据视频时长上下浮动。"
          ></Alert>
          <Form
            key="ghost"
            layout="vertical"
            classNames={{ root: 'grid gap-x-4' }}
          >
            <Watch
              control={control}
              name="remove_subtitles"
              render={(removeSubtitles) => (
                <FormControl
                  control={control}
                  name="removal_mode"
                  label="擦除模式"
                  render={({ field }) => (
                    <Select
                      {...field}
                      disabled={field.disabled || !removeSubtitles}
                      options={subtitleRemovalModeOptions}
                    />
                  )}
                />
              )}
            />
            <FormControl
              control={control}
              name="target_languages"
              extra="最多可以一次性添加 10 个目标语言"
              label="目标语言"
              render={({ field }) => {
                return (
                  <HeygenTargetLanguageSelect
                    {...field}
                    mode="multiple"
                    maxCount={10}
                  ></HeygenTargetLanguageSelect>
                );
              }}
            ></FormControl>
          </Form>
          <div className="sticky bottom-0 flex space-x-2">
            <Button
              size="large"
              variant="solid"
              color="primary"
              onClick={() => {
                reset();
                setPageData(RESET);
              }}
            >
              重置
            </Button>
            <Button
              classNames={{ root: 'sticky bottom-0' }}
              variant="solid"
              color="primary"
              size="large"
              loading={videoTranslate.isPending}
              block
              onClick={handleTranslate}
            >
              翻译视频
            </Button>
          </div>
        </div>
      </div>

      <div className="card flex-auto animate-slide-up space-y-6 sm:min-h-40 sm:flex-1 sm:overflow-auto">
        <h4 className="text-lg">🎬 视频生成状态</h4>
        <VideoGenerationTaskTable
          taskType={[TaskType.视频翻译_废弃, TaskType.视频翻译]}
        ></VideoGenerationTaskTable>
      </div>
    </div>
  );
};
